import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/** Map Cognito/Amplify error names to friendly, non-enumerating messages. */
function friendlyError(err: unknown, fallback: string): string {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'CodeMismatchException':
      return 'That code is incorrect. Please check and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one below.';
    case 'InvalidPasswordException':
      return 'Password must be at least 8 characters with upper, lower and a number.';
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a little while and try again.';
    case 'UserNotFoundException':
      // With preventUserExistenceErrors enabled this is unlikely, but stay generic.
      return 'If an account exists for that email, a code has been sent.';
    default:
      return err instanceof Error && err.message ? err.message : fallback;
  }
}

export function ForgotPasswordPage() {
  const { resetPassword, confirmResetPassword } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'request' | 'confirm' | 'done'>('request');
  const [email, setEmail] = useState('');
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const sendCode = async () => {
    const out = await resetPassword(email.trim());
    const dest = out.nextStep?.codeDeliveryDetails?.destination;
    if (dest) setDestination(dest);
  };

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await sendCode();
      setStep('confirm');
    } catch (err) {
      setError(friendlyError(err, 'Could not start the reset. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await sendCode();
      setInfo('A new code is on its way.');
    } catch (err) {
      setError(friendlyError(err, 'Could not resend the code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await confirmResetPassword(email.trim(), code.trim(), password);
      setStep('done');
    } catch (err) {
      setError(friendlyError(err, 'Could not reset your password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">Chor<i className="title-italics">tl</i><span className="title-tail">e</span></div>
          <h1 className="auth-title">Password reset</h1>
          <p className="auth-sub">Your password has been updated. You can now sign in with it.</p>
          <button className="btn btn-primary btn-full" onClick={() => navigate('/signin')}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">Chor<i className="title-italics">tl</i><span className="title-tail">e</span></div>
          <h1 className="auth-title">Reset your password</h1>
          <p className="auth-sub">
            If an account exists for <strong>{email}</strong>, we&apos;ve sent a code
            {destination ? <> to <strong>{destination}</strong></> : null}. Enter it below with your new password.
          </p>

          <form onSubmit={(e) => void handleConfirm(e)}>
            <div className="field">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </div>
            <div className="field">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Min. 8 characters"
              />
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            {info && <div className="alert alert-info">{info}</div>}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-full"
              style={{ marginTop: '0.5rem' }}
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
          </form>

          <p className="auth-footer">
            Didn&apos;t get a code?{' '}
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={loading}
              style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', font: 'inherit', fontWeight: 700, padding: 0 }}
            >
              Resend
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">Chor<i className="title-italics">tl</i><span className="title-tail">e</span></div>
        <h1 className="auth-title">Forgot password?</h1>
        <p className="auth-sub">Enter your email and we&apos;ll send you a code to reset it.</p>

        <form onSubmit={(e) => void handleRequest(e)}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary btn-full"
            style={{ marginTop: '0.5rem' }}
          >
            {loading ? 'Sending…' : 'Send reset code'}
          </button>
        </form>

        <p className="auth-footer">
          Remembered it?{' '}
          <Link to="/signin">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
